import type { SemiLoadDetails } from '../../shared/semiDriverWork';
import { driverGpsTestHeaders, isDriverGpsTestEnabled } from './driverGpsTest';
/**
 * The dispatch board's data layer.
 *
 * One board over every service line. Each line will grow its own hauler desk
 * the way waste management has one, but a driver should never have to guess
 * which desk today's work is on — everything paid and unclaimed shows up here,
 * and claiming from here is the same claim the per-line boards make.
 *
 * Thin on purpose: the server already decides what a driver may see, what they
 * clear on a job, and whether they are allowed to claim it. This just asks.
 */
import { hostWebApp } from './hostWebApp';
import { legacyAuthHeader, legacyHostName, legacySnakeKey } from '../utils/legacyHostCompat';
import { getNativeIosAuthHeaders, getNativeIosLinkedUser } from './nativeApiRouting';
import { hasActiveWebSession, getWebSessionToken, WEB_SESSION_HEADER } from './webSessionAuth';
import { flushDetention, pendingDetentionCount, queueDetention } from './dispatchOfflineQueue';
import {
  normalizeHotshotRateCard,
  platformHotshotRateCard,
  previewHotshotTakeUsd,
  type HotshotRateCard,
} from '../../shared/hotshotRateCard';

export { platformHotshotRateCard, previewHotshotTakeUsd };
export type { HotshotRateCard };

/** Same key Google / Telegram Login Widget write after browser sign-in. */
const LEGACY_SIGNED_USER_STORAGE_KEY = legacySnakeKey('user_id');

export { pendingDetentionCount };

const LEGACY_USER_ID = legacyAuthHeader('User-Id');
const LEGACY_USERNAME = legacyAuthHeader('Username');

/** Every service line the board can show, in the order the filter renders. */
export const DISPATCH_LINES = [
  { id: 'all', label: 'All', accent: '#94a3b8' },
  { id: 'hotshots', label: 'Hotshot', accent: '#ec4899' },
  { id: 'semis', label: 'Semi', accent: '#eab308' },
  { id: 'water', label: 'Water', accent: '#38bdf8' },
  { id: 'dumpsters', label: 'Dumpster', accent: '#84cc16' },
  { id: 'grocery', label: 'Grocery', accent: '#34d399' },
  { id: 'tacos_deliver', label: 'Tacos', accent: '#4ade80' },
] as const;

export type DispatchLineId = (typeof DISPATCH_LINES)[number]['id'];

/** Only the fields the board actually renders. */
export type DispatchJob = {
  id: string;
  serviceLine: string;
  serviceLabel: string;
  semiLoad?: SemiLoadDetails | null;
  status: string;
  title: string | null;
  summary: string | null;
  city: string | null;
  state: string | null;
  wellOrSite: string | null;
  deliveryAddress: string | null;
  preferredTiming: string | null;
  amountLabel: string | null;
  /**
   * Unlocked only once this driver holds the job — before that the API sends
   * "Address after claim" and nulls the rest. That reveal IS the payoff for
   * claiming, so the Mine list is where it has to show up.
   */
  contactName: string | null;
  contactPhone: string | null;
  companyName: string | null;
  latitude: number | null;
  longitude: number | null;
  /** Pickup pin when known (publicJobDto pickupLatitude). */
  pickupLatitude?: number | null;
  pickupLongitude?: number | null;
  /** A customer booked THIS viewer for this open load (card booking) — claim it. */
  reservedForViewer?: boolean;
  /** Booked for another hauler — flag it, don't offer it (until the TTL lapses). */
  reservedForOther?: boolean;
  details?: string | null;
  dropFacilityId?: string | null;
  pickupFacilityId?: string | null;
  pickupApiNumber?: string | null;
  pickupLabel?: string | null;
  bidUuid?: string | null;
  customerConfirmedAt?: string | null;
  claimedAt: string | null;
  /** Set once a company assigned this to somebody. Null on an owner-operator run. */
  assignedDriverName: string | null;
  assignedTruckLabel: string | null;
  assignedAt: string | null;
  partnerShareCents: number | null;
  partnerShareLabel: string | null;
  perMileNetLabel: string | null;
  /** Server-computed estimate; use the saved job rate and processor fee payer. */
  processorFeePayer?: 'business' | 'platform';
  amountCents?: number | null;
  platformFeeBps?: number | null;
  platformFeeCents?: number | null;
  stripeFeeCents?: number | null;
  partnerGrossCents?: number | null;
  billableMiles: number | null;
  urgency: string | null;
  createdAt: string | null;
  /** When the CUSTOMER asked. Not when the job reached the board. */
  requestedAt: string | null;
  /**
   * When the freight is actually ready — the instant every promise we sell is
   * measured from. Checkout time is NOT this: a Standard booked days ahead has
   * a ready time in the future. Null on loads booked before we captured it,
   * and a null here scores nothing rather than inventing a deadline.
   */
  readyAt: string | null;
  /** When the driver tapped Rolling. Its presence ends the roll-by question. */
  rolledAt: string | null;
  /** When the customer confirmed. Present on ?mine=1 history. */
  completedAt: string | null;
  /** Roster row id, so crew stats key on the driver and not on a display name. */
  assignedDriverId: string | null;
  /**
   * Geofence crossings, present only on ?mine=1. Their ABSENCE past a roll-by is
   * what makes a no-show a no-show.
   */
  geoEvents?: Array<{ type: string; stopKind: string | null; at: string | null }> | null;
  /** True when this job's claim demands photo proof at load and deliver. */
  proofRequired?: boolean | null;
  /** Proof-of-service: when the cargo was photographed on the truck. */
  loadedAt?: string | null;
  jsaPickupAt?: string | null;
  pickupJsaRequired?: boolean;
  dropoffJsaRequired?: boolean;
  jsaDropoffAt?: string | null;
  apiNumber?: string | null;
  /** False = the load stamp was forced/out-of-fence — flagged for review. */
  loadGpsVerified?: boolean | null;
  /** Driver's delivered tap (distinct from customer-confirmed completedAt). */
  partnerDeliveredAt?: string | null;
  /** Load/unload proof photos, present only on ?mine=1. */
  proofPhotos?: Array<{ stage: string; kind: string; url: string; at: string | null }> | null;
  /** Waiting-time clock. Null fields until somebody starts it. */
  detention: {
    pickupStartedAt: string | null;
    pickupEndedAt: string | null;
    dropStartedAt: string | null;
    dropEndedAt: string | null;
    chargeCents: number | null;
    invoiceUrl: string | null;
  } | null;
  /** Present only when the server knows where this driver is based. */
  deadhead: {
    miles: number;
    totalDrivenMiles: number;
    netPerDrivenMileCents: number | null;
    netPerDrivenMileLabel: string | null;
    belowFloor: boolean;
    farPickup: boolean;
    note: string;
  } | null;
};

export type DispatchBoard = {
  jobs: DispatchJob[];
  /** Null when nobody is signed in. */
  canClaim: boolean;
  /** Why claiming is blocked, when it is. One short line, or null. */
  claimBlockedReason: string | null;
  /** The same thing as a value, so callers never sniff the sentence. */
  claimGate: ClaimGate;
  /** True when the list is sorted nearest-pickup-first for this driver. */
  sortedByNearest: boolean;
};

/**
 * Where a device-scoped driver identity is kept.
 *
 * Its own key, not `dev-user-id`: that one is minted by hostWebApp only on
 * localhost / pages.dev / testMode and is deliberately throwaway. This one is
 * the driver's working identity on this device until sign-in ships, so the two
 * must never be confused for each other.
 */
const DEVICE_USER_ID_KEY = 'zia-device-user-id';

export type DispatchIdentity = {
  userId: string;
  username: string;
  /** Where it came from — the driver desk says so when it is device-scoped. */
  source: 'host' | 'ios' | 'browser' | 'device' | 'none';
};

/**
 * Browser Google / Telegram Login Widget principal.
 *
 * hostWebApp.getUserId() is empty outside the Telegram Mini App shell, so a
 * hauler who registers as zia-device-*, verifies email, then signs in with
 * Google had their profile rebound onto the Google principal while every
 * haul-jobs call still sent the guest device id — board: "Register to haul
 * first", toast: "Set up payouts". Prefer the signed-in id whenever we have
 * one, before minting or reusing a device guest.
 */
function browserSignedInIdentity(): { userId: string; username: string } | null {
  try {
    const raw = sessionStorage.getItem('tg_auth_user');
    if (raw) {
      const u = JSON.parse(raw) as {
        id?: string | number;
        user?: { id?: string | number; username?: string };
        username?: string;
      };
      const id = String(u?.id ?? u?.user?.id ?? '').trim();
      if (id) {
        return {
          userId: id,
          username: String(u?.username || u?.user?.username || '').trim(),
        };
      }
    }
  } catch { /* ignore */ }

  // Session header is what the server trusts; only use the legacy key when it
  // is still paired with an active web session (not a stale leftover id).
  if (hasActiveWebSession()) {
    try {
      const legacy = String(localStorage.getItem(LEGACY_SIGNED_USER_STORAGE_KEY) || '').trim();
      if (legacy) return { userId: legacy, username: '' };
    } catch { /* ignore */ }
  }
  return null;
}

/**
 * Who this device is, for every dispatch call AND for anything that shows the
 * driver their own id.
 *
 * There used to be two answers to that question. getHeaders() resolved host
 * session -> native-iOS linked user -> stored dev id, while the driver desk
 * rendered hostWebApp.getUserId() on its own. On native iOS with a linked
 * account those differ, so the id a dispatcher was told to type into the roster
 * was not the id ?mine=1 matched on — and that driver's desk stayed empty
 * forever with nothing on screen to explain it.
 *
 * One function now, used by both. The id shown IS the id sent.
 *
 * Order: Telegram host → native iOS link → browser signed-in session → device guest.
 */
export function resolveDispatchIdentity(): DispatchIdentity {
  let userId = hostWebApp.getUserId?.() || '';
  let username =
    (window as unknown as Record<string, { WebApp?: { initDataUnsafe?: { user?: { username?: string } } } }>)[
      legacyHostName()
    ]?.WebApp?.initDataUnsafe?.user?.username || '';
  let source: DispatchIdentity['source'] = userId ? 'host' : 'none';

  // A linked native account outranks the host session — same precedence this
  // has always had, just no longer duplicated anywhere else.
  const linked = getNativeIosLinkedUser();
  if (linked?.userId) {
    userId = linked.userId;
    source = 'ios';
    if (linked.username) username = linked.username;
  }

  if (!userId) {
    const browser = browserSignedInIdentity();
    if (browser?.userId) {
      userId = browser.userId;
      source = 'browser';
      if (browser.username) username = browser.username;
    }
  }

  if (!userId) {
    /*
     * Nothing signed in. Mint one and keep it.
     *
     * The board deliberately has no auth gate until it is A-grade, but a driver
     * still needs to hold loads, be added to a crew and be told their own id.
     * Without this, production returns no id at all: hostWebApp only mints one
     * on localhost / pages.dev / testMode. So the driver desk rendered nothing
     * where the id should be, while the crew card three taps away demanded it.
     *
     * Adopt an existing dev id rather than minting beside it, so nobody testing
     * today is orphaned from the loads they are already holding.
     */
    try {
      const dev = localStorage.getItem('dev-user-id') || '';
      const stored = localStorage.getItem(DEVICE_USER_ID_KEY) || dev;
      if (stored) {
        userId = stored;
      } else {
        userId = `zia-device-${
          typeof crypto !== 'undefined' && 'randomUUID' in crypto
            ? crypto.randomUUID().replace(/-/g, '').slice(0, 16)
            : Math.random().toString(36).slice(2, 12)
        }`;
      }
      if (userId && localStorage.getItem(DEVICE_USER_ID_KEY) !== userId) {
        localStorage.setItem(DEVICE_USER_ID_KEY, userId);
      }
      if (userId) source = 'device';
    } catch {
      // Private browsing with storage denied. No identity, and the desk says so
      // rather than showing an id that will not survive the next request.
      return { userId: '', username, source: 'none' };
    }
  }

  if (!username) {
    try { username = localStorage.getItem('dev-username') || ''; } catch { /* denied */ }
  }
  return { userId, username, source };
}

/**
 * The identity headers every haul call sends, for anyone who needs them.
 *
 * Exported because the hauler signup form was posting bare Content-Type and
 * nothing else — so the server saw an anonymous application, wrote
 * claimed_user_id NULL, and skipped creating the haul profile. Nine real
 * applications on file, no profile behind any of them, and the person who had
 * just signed up was told to sign up again.
 */
export function dispatchIdentityHeaders(): Record<string, string> {
  return getHeaders();
}

function getHeaders(): Record<string, string> {
  const { userId, username } = resolveDispatchIdentity();

  const headers: Record<string, string> = { 'Content-Type': 'application/json', ...driverGpsTestHeaders() };
  if (userId) {
    headers['X-Telegram-User-Id'] = userId;
    headers['X-Web-User-Id'] = userId;
    headers[LEGACY_USER_ID] = userId;
  }
  if (username) {
    headers['X-Telegram-Username'] = username;
    headers['X-Web-Username'] = username;
    headers[LEGACY_USERNAME] = username;
  }
  const token = getWebSessionToken();
  if (token) headers[WEB_SESSION_HEADER] = token;
  const initData = hostWebApp.getInitData?.();
  if (initData) headers['X-Telegram-Init-Data'] = initData;
  Object.assign(headers, getNativeIosAuthHeaders() || {});
  return headers;
}

/**
 * Why this driver cannot take work yet, in one line, or null if they can.
 *
 * ONLY answers when the server actually told us something about them. No
 * partnerConnect block means we do not know who is looking — and an unknown
 * viewer is not a blocked one. Guessing "sign in to claim" there greys out the
 * board's whole reason to exist for anyone browsing, and it invents a rule the
 * server has not stated. Let them tap; the claim comes back with the real
 * reason, which is always better copy than ours.
 *
 * When the server DOES describe them, name the one next step and no more.
 */
function claimBlockedReason(payload: {
  driverVerification?: { ready: boolean; reason?: string | null };
  connectRequiredToClaim?: boolean;
  partnerConnect?: { hasProfile?: boolean; connectReady?: boolean; canClaim?: boolean } | null;
}): string | null {
  if (payload.driverVerification?.ready === false) return payload.driverVerification.reason || 'Finish driver verification before accepting deliveries.';
  const pc = payload.partnerConnect;
  if (!pc) return null;
  if (!pc.hasProfile) return 'Apply to haul — takes about two minutes';
  if (payload.connectRequiredToClaim && !pc.connectReady) {
    return 'One step left: finish bank setup so we can pay you';
  }
  // Was "Not approved to claim yet". Nothing approves anyone — applying creates
  // the profile and Stripe decides the rest — so that sentence described a
  // queue that does not exist and left the reader waiting for a call.
  return pc.canClaim ? null : 'Your approved service lines do not cover this work';
}

/**
 * The same answer, as a value rather than a sentence.
 *
 * The board decided which button to show with `reason.includes('bank')`, so the
 * day that copy changed it would have silently offered the wrong one. Callers
 * branch on this; the sentence is only ever for reading.
 */
export type ClaimGate = 'apply' | 'bank' | 'lines' | 'verify' | null;

export function claimGate(payload: {
  driverVerification?: { ready: boolean; reason?: string | null };
  connectRequiredToClaim?: boolean;
  partnerConnect?: { hasProfile?: boolean; connectReady?: boolean; canClaim?: boolean } | null;
}): ClaimGate {
  if (payload.driverVerification?.ready === false) return 'verify';
  const pc = payload.partnerConnect;
  if (!pc) return null;
  if (!pc.hasProfile) return 'apply';
  if (payload.connectRequiredToClaim && !pc.connectReady) return 'bank';
  return pc.canClaim ? null : 'lines';
}

/** Open jobs, optionally narrowed to one line. */
export async function fetchDispatchBoard(
  line: DispatchLineId = 'all',
  limit = 50,
): Promise<DispatchBoard> {
  const params = new URLSearchParams();
  if (line !== 'all') params.set('service', line);
  params.set('limit', String(limit));

  const res = await fetch(`/api/haul-jobs?${params.toString()}`, {
    headers: getHeaders(),
  });
  const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  if (!res.ok || !data.success) {
    throw new Error(String(data.error || 'Could not load the dispatch board'));
  }

  const viewerBase = data.viewerBase as { sortedByNearest?: boolean } | undefined;
  return {
    jobs: (Array.isArray(data.jobs) ? data.jobs : []) as DispatchJob[],
    canClaim: claimBlockedReason(data as never) == null,
    claimBlockedReason: claimBlockedReason(data as never),
    claimGate: claimGate(data as never),
    sortedByNearest: Boolean(viewerBase?.sortedByNearest),
  };
}

/** The jobs this driver currently holds, contact details unlocked. */
export async function fetchMyDispatchJobs(limit = 50, line?: DispatchLineId): Promise<DispatchJob[]> {
  const service = line && line !== 'all' ? `&service=${encodeURIComponent(line)}` : '';
  const res = await fetch(`/api/haul-jobs?mine=1&limit=${limit}${service}`, { headers: getHeaders() });
  const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  if (!res.ok || !data.success) {
    throw new Error(String(data.error || 'Could not load your jobs'));
  }
  return (Array.isArray(data.jobs) ? data.jobs : []) as DispatchJob[];
}

/**
 * Open jobs a customer booked for this hauler, not yet claimed (within the TTL).
 * Tolerant of a signed-out caller (→ []), so the board never errors over it.
 */
/** Jobs sitting on a hauler this driver is rostered under (not yet theirs). */
export async function fetchCompanyDispatchJobs(
  line: DispatchLineId = 'all',
  limit = 30,
): Promise<DispatchJob[]> {
  try {
    const params = new URLSearchParams({ company: '1', limit: String(limit) });
    if (line !== 'all') params.set('service', line);
    const res = await fetch(`/api/haul-jobs?${params.toString()}`, { headers: getHeaders() });
    const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
    if (res.ok && data.success && Array.isArray(data.jobs)) return data.jobs as DispatchJob[];
  } catch {
    /* offline / signed out */
  }
  return [];
}

export async function fetchReservedForMe(limit = 20): Promise<DispatchJob[]> {
  try {
    const res = await fetch(`/api/haul-jobs?reserved=1&limit=${limit}`, { headers: getHeaders() });
    const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
    if (res.ok && data.success && Array.isArray(data.jobs)) return data.jobs as DispatchJob[];
  } catch {
    /* offline / signed out */
  }
  return [];
}

/** One durable haul notification (booked / open / claimed / paid …). */
export type HaulInboxItem = {
  id: string;
  jobId: string | null;
  eventType: string;
  title: string;
  body: string | null;
  payload: Record<string, unknown> | null;
  createdAt: string | null;
};

/**
 * The durable notification inbox every haul notify writes
 * (GET /api/haul-jobs?inbox=1). Tolerant of a signed-out caller (→ []) — the
 * poller must never surface an auth error as UI noise.
 */
export async function fetchHaulInbox(since?: string | null, limit = 30): Promise<HaulInboxItem[]> {
  try {
    const params = new URLSearchParams({ inbox: '1', limit: String(limit) });
    if (since) params.set('since', since);
    const res = await fetch(`/api/haul-jobs?${params.toString()}`, { headers: getHeaders() });
    const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
    if (res.ok && data.success && Array.isArray(data.notifications)) {
      return data.notifications as HaulInboxItem[];
    }
  } catch {
    /* offline / signed out */
  }
  return [];
}

/**
 * Take a job.
 *
 * Returns the claimed job, because the server unlocks the address and phone in
 * the same response — a driver should not have to make a second round trip to
 * find out where they are going.
 *
 * The server owns the race: two drivers tapping at once means one gets it and
 * the other is told so. We surface that message rather than inventing our own.
 */
/**
 * Why a claim was refused, not just that it was.
 *
 * The server distinguishes "you have no hauler profile" from "your bank is not
 * linked" and names the path out of each. This threw all of it away and
 * returned a sentence, so the board could only print text with no button —
 * telling a driver to do something while offering no way to do it.
 */
export type ClaimBlock = 'partner' | 'connect' | 'verify' | null;

export async function claimDispatchJob(
  jobId: string,
): Promise<
  | { ok: true; job: DispatchJob | null }
  | { ok: false; error: string; blocked: ClaimBlock; reserved?: boolean }
> {
  try {
    let coords: { latitude: number; longitude: number } | null = null;
    try {
      const fix = await acquireDriverFix();
      if (fix.ok) {
        coords = { latitude: fix.pos.coords.latitude, longitude: fix.pos.coords.longitude };
      }
    } catch { /* grocery accept price still works without GPS */ }
    const res = await fetch('/api/haul-jobs', {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ action: 'claim', jobId, proofCapable: true, ...coords }),
    });
    const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
    if (!res.ok || !data.success) {
      return {
        ok: false,
        error: String(data.error || 'Could not claim that job'),
        blocked: data.driverVerificationRequired ? 'verify' : data.partnerRequired ? 'partner' : data.connectRequired ? 'connect' : null,
        reserved: data.reserved === true,
      };
    }
    return { ok: true, job: (data.job as DispatchJob) || null };
  } catch {
    return { ok: false, error: 'No connection — try again', blocked: null };
  }
}

/** Dial a driver's contact. Digits only — a formatted string breaks tel:. */
export function telHref(phone: string | null | undefined): string | null {
  const digits = String(phone || '').replace(/\D/g, '');
  return digits.length >= 10 ? `tel:${digits}` : null;
}

/**
 * Turn-by-turn to the job.
 *
 * Prefers the pin, because a pad has no street address worth typing. Falls back
 * to the address text when there is no pin.
 */
export function mapsHref(job: {
  latitude?: number | null;
  longitude?: number | null;
  deliveryAddress?: string | null;
}): string | null {
  const lat = Number(job.latitude);
  const lng = Number(job.longitude);
  if (Number.isFinite(lat) && Number.isFinite(lng) && (lat !== 0 || lng !== 0)) {
    return `https://maps.apple.com/?daddr=${lat},${lng}`;
  }
  const addr = String(job.deliveryAddress || '').trim();
  if (!addr || addr.toLowerCase().startsWith('address after')) return null;
  return `https://maps.apple.com/?daddr=${encodeURIComponent(addr)}`;
}

/** Navigate to pickup pin when the mine payload includes it. */
export function mapsHrefPickup(job: {
  pickupLatitude?: number | null;
  pickupLongitude?: number | null;
}): string | null {
  const lat = Number(job.pickupLatitude);
  const lng = Number(job.pickupLongitude);
  if (Number.isFinite(lat) && Number.isFinite(lng) && (lat !== 0 || lng !== 0)) {
    return `https://maps.apple.com/?daddr=${lat},${lng}`;
  }
  return null;
}


/**
 * "I'm rolling."
 *
 * The only honest record of when the truck left, and the thing every promise we
 * sell is scored against. It cannot be inferred: `departed_pickup` fires on any
 * later ping outside the pickup fence, so it records the DROP arrival.
 *
 * Posted live, never queued. The offline queue treats any 4xx as done, so a roll
 * taken in a dead zone and replayed after Delivered would 409 on the status
 * check and be silently discarded — the one case the queue exists for is the one
 * it would destroy. On failure the driver is told to tap again.
 */
export async function markDispatchJobRolling(
  jobId: string,
): Promise<{ ok: true; rolledAt: string; message: string } | { ok: false; error: string }> {
  try {
    const res = await fetch('/api/haul-jobs', {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ action: 'rolling', jobId }),
    });
    const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
    if (!res.ok || !data.success) {
      return { ok: false, error: String(data.error || 'Could not mark that rolling — tap again') };
    }
    return {
      ok: true,
      rolledAt: String(data.rolledAt || ''),
      message: String(data.message || 'Rolling.'),
    };
  } catch {
    return { ok: false, error: 'No connection — tap again when you have signal' };
  }
}

/**
 * One position fix, with driver-comprehensible failures. Shared by check-in,
 * the load proof and the delivered proof — the same fix, taken when the driver
 * says so; never a background tracker.
 */
async function acquireDriverFix(): Promise<
  { ok: true; pos: GeolocationPosition } | { ok: false; error: string }
> {
  // Deliberately unusable GPS; the server substitutes its saved pin only after authorization.
  if (isDriverGpsTestEnabled()) return { ok: true, pos: { coords: { latitude: 0, longitude: 0, accuracy: 999999999 } } as GeolocationPosition };
  if (typeof navigator === 'undefined' || !navigator.geolocation) {
    return { ok: false, error: 'This device cannot share a location.' };
  }
  try {
    const pos = await new Promise<GeolocationPosition>((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(resolve, reject, {
        enableHighAccuracy: true,
        timeout: 15_000,
        maximumAge: 30_000,
      });
    });
    return { ok: true, pos };
  } catch (e) {
    const code = (e as GeolocationPositionError | undefined)?.code;
    if (code === 1) return { ok: false, error: 'Location is off for this app — turn it on to check in.' };
    if (code === 3) return { ok: false, error: 'Could not get a fix. Step outside and try again.' };
    return { ok: false, error: 'Could not read your location.' };
  }
}

/**
 * Upload one proof photo slot (already compressed to a data URL). Returns the
 * photoId the step call must carry — uploading alone advances nothing.
 */
export async function uploadHaulProofPhoto(
  jobId: string,
  stage: 'load' | 'unload',
  kind: string,
  dataUrl: string,
): Promise<{ ok: true; photoId: string; url: string } | { ok: false; error: string }> {
  try {
    const res = await fetch('/api/haul-jobs/proof-photo', {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ jobId, stage, kind, dataUrl }),
    });
    const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
    if (!res.ok || !data.success) {
      return { ok: false, error: String(data.error || 'Could not save the photo — try again') };
    }
    return { ok: true, photoId: String(data.photoId || ''), url: String(data.url || '') };
  } catch {
    return { ok: false, error: 'No connection — try again when you have signal' };
  }
}

/**
 * Cargo is on the truck: photos first, then this — it GPS-stamps the load and
 * tells the customer (with the photos) that their freight is moving.
 */
export async function markDispatchJobLoaded(
  jobId: string,
  photoIds: string[],
  opts: { forceGps?: boolean } = {},
): Promise<
  | { ok: true; loadedAt: string; gpsVerified: boolean | null; message: string }
  | { ok: false; error: string }
> {
  const fix = await acquireDriverFix();
  if (!fix.ok) return fix;
  try {
    const res = await fetch('/api/haul-jobs', {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({
        action: 'loaded',
        jobId,
        photoIds,
        latitude: fix.pos.coords.latitude,
        longitude: fix.pos.coords.longitude,
        accuracy: fix.pos.coords.accuracy,
        forceGps: Boolean(opts.forceGps),
      }),
    });
    const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
    if (!res.ok || !data.success) {
      return { ok: false, error: String(data.error || 'Could not mark that loaded') };
    }
    return {
      ok: true,
      loadedAt: String(data.loadedAt || ''),
      gpsVerified: data.gpsVerified == null ? null : Boolean(data.gpsVerified),
      message: String(data.message || 'Loaded.'),
    };
  } catch {
    return { ok: false, error: 'No connection — try again' };
  }
}

/**
 * Mark it delivered.
 *
 * Does not complete the job — the customer confirms, then the payout releases.
 * The driver is told exactly that, in the server's own words, so nobody is
 * surprised when the money does not move the second they drop the load.
 *
 * Proofed jobs (claimed after the proof cutover) must carry the two unload
 * photoIds; this then also takes a GPS fix so an in-fence delivery verifies
 * the arrival the escrow gate checks.
 */
export async function markDispatchJobDelivered(
  jobId: string,
  opts: { photoIds?: string[] } = {},
): Promise<{ ok: true; job: DispatchJob | null; message: string } | { ok: false; error: string }> {
  const withProof = Array.isArray(opts.photoIds) && opts.photoIds.length > 0;
  let coords: { latitude: number; longitude: number; accuracy: number } | null = null;
  if (withProof) {
    const fix = await acquireDriverFix();
    if (!fix.ok) return fix;
    coords = {
      latitude: fix.pos.coords.latitude,
      longitude: fix.pos.coords.longitude,
      accuracy: fix.pos.coords.accuracy,
    };
  }
  try {
    const res = await fetch('/api/haul-jobs', {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({
        action: 'complete',
        jobId,
        ...(withProof ? { photoIds: opts.photoIds, ...coords } : {}),
      }),
    });
    const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
    if (!res.ok || !data.success) {
      return { ok: false, error: String(data.error || 'Could not mark that delivered') };
    }
    return {
      ok: true,
      job: (data.job as DispatchJob) || null,
      message: String(data.message || 'Marked delivered.'),
    };
  } catch {
    return { ok: false, error: 'No connection — try again' };
  }
}

export type DetentionEnd = 'pickup' | 'drop';

/**
 * Start or stop the waiting clock.
 *
 * Stopping prices the wait and returns what the customer now owes, so the
 * driver sees their waiting turn into money at the moment they stop the clock
 * rather than on a statement three weeks later.
 */
export async function setDetentionClock(
  jobId: string,
  end: DetentionEnd,
  action: 'start' | 'stop',
  atISO: string = new Date().toISOString(),
): Promise<
  | { ok: true; startedAt?: string; owedNowCents?: number; message?: string; queued?: false }
  | { ok: true; queued: true; message: string }
  | { ok: false; error: string }
> {
  // Offline: hold it with the time it actually happened. An hour of waiting is
  // $75 and it is owed from the tap, not from whenever the phone finds signal.
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    queueDetention({ jobId, end, action, atISO });
    return {
      ok: true,
      queued: true,
      message: action === 'stop'
        ? 'No signal — wait saved and will send when you are back in service.'
        : 'No signal — clock started and will send when you are back in service.',
    };
  }
  try {
    const res = await fetch('/api/haul-jobs/detention', {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ jobId, end, action, atISO }),
    });
    const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
    if (!res.ok || !data.success) {
      return { ok: false, error: String(data.error || 'Could not update the wait clock') };
    }
    return {
      ok: true,
      startedAt: data.startedAt != null ? String(data.startedAt) : undefined,
      owedNowCents: data.owedNowCents != null ? Number(data.owedNowCents) : undefined,
      message: data.message != null ? String(data.message) : undefined,
    };
  } catch {
    // The request itself died — same as being offline, and the same reason it
    // must not be lost.
    queueDetention({ jobId, end, action, atISO });
    return {
      ok: true,
      queued: true,
      message: 'Could not reach us — wait saved and will send when signal is back.',
    };
  }
}

/** Replay anything the queue is holding. Safe to call often; a no-op when empty. */
export async function flushDispatchQueue(): Promise<{ sent: number; kept: number }> {
  return flushDetention(async (item) => {
    const res = await fetch('/api/haul-jobs/detention', {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({
        jobId: item.jobId,
        end: item.end,
        action: item.action,
        atISO: item.atISO,
      }),
    });
    const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
    // A 401 is not a verdict on the wait — it means the session lapsed (the
    // server now requires verified identity). Dropping the item here would
    // silently discard billable wait time, so keep it queued; it sends after
    // the driver signs back in.
    if (res.status === 401) return false;
    // Any other 4xx the server will never accept must not retry forever —
    // treat a definitive rejection as done. Transport/5xx failures stay queued.
    if (res.status >= 400 && res.status < 500) return true;
    return Boolean(res.ok && data.success);
  });
}

/**
 * Which end the clock is (or should be) timing, and whether it is running.
 *
 * Pickup first, then drop. The driver never picks an end from a menu — waiting
 * happens in a fixed order and asking them to classify it while they sit at a
 * gate is a question with an obvious answer.
 */
export function detentionState(job: DispatchJob): {
  end: DetentionEnd;
  running: boolean;
  startedAt: string | null;
} {
  const d = job.detention;
  if (!d) return { end: 'pickup', running: false, startedAt: null };
  if (d.pickupStartedAt && !d.pickupEndedAt) {
    return { end: 'pickup', running: true, startedAt: d.pickupStartedAt };
  }
  if (d.dropStartedAt && !d.dropEndedAt) {
    return { end: 'drop', running: true, startedAt: d.dropStartedAt };
  }
  // Pickup already timed and stopped → the next wait is at the drop.
  return { end: d.pickupStartedAt ? 'drop' : 'pickup', running: false, startedAt: null };
}

/** mm:ss-ish elapsed label. Minutes only past an hour — nobody watches seconds tick for 90 minutes. */
export function elapsedLabel(startedAt: string | null | undefined, nowMs: number): string {
  const start = startedAt ? Date.parse(startedAt) : NaN;
  if (!Number.isFinite(start)) return '0:00';
  const secs = Math.max(0, Math.floor((nowMs - start) / 1000));
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}` : `${m}:${String(s).padStart(2, '0')}`;
}


/**
 * "Aug 6, 8:13 AM · 37h ago"
 *
 * Both halves earn their place: the stamp answers when, the age answers whether
 * this load has been sitting. A three-hour-old run and a two-minute-old one read
 * identically without it.
 */
export function requestedLabel(iso: string | null | undefined, nowMs: number): string | null {
  if (!iso) return null;
  const ms = Date.parse(String(iso));
  if (!Number.isFinite(ms)) return null;

  const when = new Date(ms).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });

  const mins = Math.max(0, Math.floor((nowMs - ms) / 60000));
  let age: string;
  if (mins < 1) age = 'just now';
  else if (mins < 60) age = `${mins}m ago`;
  else if (mins < 60 * 48) age = `${Math.floor(mins / 60)}h ago`;
  else age = `${Math.floor(mins / 1440)}d ago`;

  return `${when} · ${age}`;
}


export type ArrivalResult = {
  /** Geofences the ping just tripped, e.g. arrived_pickup. */
  newEvents: string[];
  /** How far the driver is from each stop, and whether that counts as there. */
  distances: Array<{ kind: string; meters: number; inside: boolean }>;
};

/**
 * "I'm here."
 *
 * One position fix, posted to the location action the server has always had.
 * That path fires the geofences, writes the stop event and texts the site
 * contact — machinery that has been built and switched on this whole time with
 * nothing in the app ever sending it a coordinate.
 *
 * Deliberately not a background tracker. This is one fix, taken when a driver
 * says so: no permission surprise, no battery drain, nothing running after the
 * job ends.
 */
export async function pingDispatchLocation(
  jobId: string,
  coords: { latitude: number; longitude: number; accuracy?: number | null },
  opts: { gpsTest?: boolean } = {},
): Promise<{ ok: true; result: ArrivalResult } | { ok: false; error: string }> {
  try {
    const headers = getHeaders();
    if (!opts.gpsTest) delete headers['X-ZiaWellNav-Gps-Test'];
    const res = await fetch('/api/haul-jobs', {
      method: 'POST',
      headers,
      body: JSON.stringify({
        action: 'location',
        jobId,
        latitude: coords.latitude,
        longitude: coords.longitude,
        accuracy: coords.accuracy,
      }),
    });
    const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
    if (!res.ok || !data.success) {
      return { ok: false, error: String(data.error || 'Could not check you in') };
    }
    return {
      ok: true,
      result: {
        newEvents: Array.isArray(data.newEvents) ? (data.newEvents as string[]) : [],
        distances: Array.isArray(data.distances)
          ? (data.distances as ArrivalResult['distances'])
          : [],
      },
    };
  } catch {
    return { ok: false, error: 'No connection — try again' };
  }
}

export async function markArrived(jobId: string): Promise<
  { ok: true; result: ArrivalResult } | { ok: false; error: string }
> {
  const fix = await acquireDriverFix();
  if (!fix.ok) return fix;
  return pingDispatchLocation(jobId, {
    latitude: fix.pos.coords.latitude,
    longitude: fix.pos.coords.longitude,
    accuracy: fix.pos.coords.accuracy,
  }, { gpsTest: isDriverGpsTestEnabled() });
}

export async function stampHaulJsa(
  jobId: string,
  stop: 'pickup' | 'dropoff',
  jsaId: string,
): Promise<{ ok: true } | { ok: false; error: string }> {
  try {
    const res = await fetch('/api/haul-jobs', {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ action: 'jsa', jobId, stop, jsaId }),
    });
    const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
    if (!res.ok || !data.success) {
      return { ok: false, error: String(data.error || 'Could not save JSA') };
    }
    return { ok: true };
  } catch {
    return { ok: false, error: 'No connection — try again' };
  }
}

/**
 * What to tell the driver after a check-in.
 *
 * Tripping a geofence is the good case. Not tripping one is not an error — they
 * are simply not there yet — so say how far off they are instead of failing at
 * them. Anything else and they will tap it repeatedly wondering what broke.
 */
export function arrivalMessage(result: ArrivalResult): string {
  if (result.newEvents.includes('arrived_pickup')) return 'Checked in at the load site.';
  if (result.newEvents.includes('arrived_dropoff')) return 'Checked in at the drop.';

  const nearest = result.distances
    .filter((d) => Number.isFinite(d.meters))
    .sort((a, b) => a.meters - b.meters)[0];
  if (!nearest) return 'Location sent.';
  if (nearest.inside) return 'Already checked in here.';

  const miles = nearest.meters / 1609.34;
  const away = miles >= 0.2
    ? `${miles.toFixed(1)} mi`
    : `${Math.round(nearest.meters)} m`;
  return `Not on site yet — ${away} from the ${nearest.kind === 'pickup' ? 'pickup' : 'drop'}.`;
}


/**
 * Start Stripe Connect onboarding.
 *
 * The board tells a driver they need bank setup and, until now, gave them
 * nowhere to do it — an instruction with no door. Returns the URL to send them
 * to, or `partnerRequired` when the real blocker is one step earlier: they have
 * no approved hauler profile yet, and connecting a bank cannot fix that.
 */
export async function startHaulerConnect(): Promise<
  | { ok: true; onboardingUrl: string }
  | { ok: false; partnerRequired?: boolean; loginRequired?: boolean; error: string }
> {
  try {
    const res = await fetch('/api/partner-haul/connect', {
      method: 'POST',
      headers: getHeaders(),
    });
    const data = (await res.json().catch(() => ({}))) as {
      success?: boolean;
      error?: string;
      onboardingUrl?: string;
      partnerRequired?: boolean;
      loginRequired?: boolean;
    };
    if (!res.ok || !data.success || !data.onboardingUrl) {
      return {
        ok: false,
        partnerRequired: Boolean(data.partnerRequired),
        loginRequired: Boolean(data.loginRequired),
        error: data.error
          || (data.partnerRequired
            ? 'Apply to haul first — it only takes a couple of minutes.'
            : 'Could not start bank setup.'),
      };
    }
    return { ok: true, onboardingUrl: data.onboardingUrl };
  } catch {
    return { ok: false, error: 'No connection — try again' };
  }
}


export type HaulerPayout = {
  id: string;
  jobId: string | null;
  serviceLine: string;
  title: string | null;
  where: string | null;
  netCents: number;
  status: string;
  paidAt: string | null;
  note: string | null;
  /** Why the money has not moved yet. Null once it has. */
  transferError: string | null;
  /** False once retrying is pointless — a human has to look. */
  canRetry: boolean;
  createdAt: string | null;
};

export type HaulerEarnings = {
  signedIn: boolean;
  pendingCents: number;
  /** Legacy name: recorded Connect transfers, not verified bank deposits. */
  paidCents: number;
  error?: string;
  payouts: HaulerPayout[];
};

/**
 * What this driver has earned.
 *
 * Anonymous is not an error — it is zero. The board does not require signing
 * in, so an earnings panel that demanded it would contradict the screen it
 * sits on.
 */
export async function fetchHaulerEarnings(): Promise<HaulerEarnings> {
  const empty: HaulerEarnings = { signedIn: false, pendingCents: 0, paidCents: 0, payouts: [] };
  try {
    const res = await fetch('/api/partner-haul/payouts', { headers: getHeaders() });
    const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
    if (!res.ok || !data.success || typeof data.signedIn !== 'boolean'
      || !Number.isSafeInteger(data.pendingCents) || Number(data.pendingCents) < 0
      || !Number.isSafeInteger(data.paidCents) || Number(data.paidCents) < 0
      || !Array.isArray(data.payouts)) {
      return { ...empty, error: 'Earnings could not be loaded. Please try again.' };
    }
    return {
      signedIn: Boolean(data.signedIn),
      pendingCents: Number(data.pendingCents) || 0,
      paidCents: Number(data.paidCents) || 0,
      payouts: Array.isArray(data.payouts) ? (data.payouts as HaulerPayout[]) : [],
    };
  } catch {
    return { ...empty, error: 'Earnings could not be loaded. Check your connection and try again.' };
  }
}


/**
 * Collect a payout that did not go through.
 *
 * Almost always the same story: the driver had not finished bank setup when
 * the customer confirmed. They finish it, tap this, and get paid without
 * anybody in the loop.
 */
export async function retryHaulerPayout(
  payoutId: string,
): Promise<{ ok: true; transferId: string } | { ok: false; error: string }> {
  try {
    const res = await fetch('/api/partner-haul/payouts', {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ action: 'retry', payoutId }),
    });
    const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
    if (!res.ok || !data.success) {
      return { ok: false, error: String(data.error || 'Could not send that payout') };
    }
    return { ok: true, transferId: String(data.transferId || '') };
  } catch {
    return { ok: false, error: 'No connection — try again' };
  }
}

/**
 * The hauler's own rate card (Phase 4a — config-only, not yet wired to quotes).
 * Anonymous / no card → the platform defaults, so the Rates lane always paints.
 */
export async function getHotshotRateCard(): Promise<{ rateCard: HotshotRateCard; source: 'company' | 'platform' }> {
  try {
    const res = await fetch('/api/partner-haul/rate-card', { headers: getHeaders() });
    const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
    if (res.ok && data.success && data.rateCard) {
      return {
        rateCard: normalizeHotshotRateCard(data.rateCard),
        source: data.source === 'company' ? 'company' : 'platform',
      };
    }
  } catch {
    /* fall through to platform */
  }
  return { rateCard: platformHotshotRateCard(), source: 'platform' };
}

export async function saveHotshotRateCard(
  card: HotshotRateCard,
  meta?: { companyName?: string; crewId?: string },
): Promise<{ ok: true; rateCard: HotshotRateCard } | { ok: false; error: string; needsSignIn?: boolean }> {
  try {
    const res = await fetch('/api/partner-haul/rate-card', {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ card, companyName: meta?.companyName, crewId: meta?.crewId }),
    });
    const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
    if (res.ok && data.success && data.rateCard) {
      return { ok: true, rateCard: normalizeHotshotRateCard(data.rateCard) };
    }
    return { ok: false, error: String(data.error || 'Could not save your rates'), needsSignIn: Boolean(data.needsSignIn) };
  } catch {
    return { ok: false, error: 'No connection — try again when you have signal' };
  }
}


// ─── Hauler: crew and taking work ────────────────────────────────────────────

export type CompanyDriver = {
  id: string;
  /** Never sent by the server — the owner links by CODE and never sees an id. */
  driverUserId?: never;
  name: string;
  phone: string | null;
  truckLabel: string | null;
  trailerClass: string | null;
  baseCity: string | null;
  status: string;
  /** False until they have an app account — they can be assigned, not work it. */
  linked: boolean;
  createdAt: string | null;
};

export async function fetchCompanyDrivers(): Promise<{
  signedIn: boolean;
  drivers: CompanyDriver[];
}> {
  try {
    const res = await fetch('/api/haul-jobs/drivers', { headers: getHeaders() });
    const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
    if (!res.ok || !data.success) return { signedIn: false, drivers: [] };
    return {
      signedIn: Boolean(data.signedIn),
      drivers: Array.isArray(data.drivers) ? (data.drivers as CompanyDriver[]) : [],
    };
  } catch {
    return { signedIn: false, drivers: [] };
  }
}

/**
 * Every load in a date range, with only the columns the promise clock scores.
 *
 * Not `?mine=1`: that caps at 50 rows with no date filter, so a busy week
 * truncates to the last day and a half and still calls itself the week.
 */
export type PromiseRecordJob = {
  id: string;
  status: string | null;
  urgency: string | null;
  readyAt: string | null;
  rolledAt: string | null;
  details?: string | null;
  dropFacilityId?: string | null;
  pickupFacilityId?: string | null;
  pickupApiNumber?: string | null;
  pickupLabel?: string | null;
  bidUuid?: string | null;
  customerConfirmedAt?: string | null;
  claimedAt: string | null;
  assignedAt: string | null;
  assignedDriverId: string | null;
  assignedDriverName: string | null;
  completedAt: string | null;
  requestedAt: string | null;
  title: string | null;
  where: string | null;
};

export async function fetchPromiseRecord(
  fromIso?: string,
  toIso?: string,
): Promise<{
  signedIn: boolean;
  jobs: PromiseRecordJob[];
  from: string | null;
  to: string | null;
  truncated: boolean;
}> {
  const qs = new URLSearchParams();
  if (fromIso) qs.set('from', fromIso);
  if (toIso) qs.set('to', toIso);
  const empty = { signedIn: false, jobs: [], from: null, to: null, truncated: false };
  try {
    const res = await fetch(`/api/haul-jobs/promises?${qs.toString()}`, { headers: getHeaders() });
    const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
    if (!res.ok || !data.success) return empty;
    return {
      signedIn: Boolean(data.signedIn),
      jobs: Array.isArray(data.jobs) ? (data.jobs as PromiseRecordJob[]) : [],
      from: data.from != null ? String(data.from) : null,
      to: data.to != null ? String(data.to) : null,
      truncated: Boolean(data.truncated),
    };
  } catch {
    return empty;
  }
}

/**
 * "Here's my code" — the driver's half of getting on a crew.
 *
 * Replaces reading a raw user id aloud. That id is what X-Web-User-Id carries
 * and nothing verifies that header, so it is closer to a password than a
 * username; a code resolves to it once, on the server, and then dies.
 */
export async function requestDriverLinkCode(
  name?: string,
): Promise<{ ok: true; code: string; expiresAt: string } | { ok: false; error: string }> {
  try {
    const res = await fetch('/api/haul-jobs/link-code', {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ name: name || '' }),
    });
    const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
    if (!res.ok || !data.success) {
      return { ok: false, error: String(data.error || 'Could not make a code — try again') };
    }
    return { ok: true, code: String(data.code || ''), expiresAt: String(data.expiresAt || '') };
  } catch {
    return { ok: false, error: 'No connection — try again when you have signal' };
  }
}

/**
 * "I'll do the bank later."
 *
 * Records when they stepped away so a reminder can tell somebody who deferred
 * an hour ago from somebody who deferred a fortnight ago. Fire-and-forget: a
 * person trying to leave a form must never be blocked by bookkeeping.
 */
export async function deferHaulerOnboarding(): Promise<void> {
  try {
    await fetch('/api/partner-haul/defer', { method: 'POST', headers: getHeaders() });
  } catch {
    /* the reminder is a nicety; leaving must always work */
  }
}

export type DriverInput = {
  name?: string;
  phone?: string;
  truckLabel?: string;
  trailerClass?: string | null;
  baseCity?: string;
  /** Six characters the driver read out. Resolved and burned server-side. */
  linkCode?: string;
  status?: string;
};

export async function saveCompanyDriver(
  input: DriverInput & { id?: string },
): Promise<{ ok: true; driver: CompanyDriver | null } | { ok: false; error: string }> {
  try {
    const editing = Boolean(input.id);
    const res = await fetch('/api/haul-jobs/drivers', {
      method: editing ? 'PATCH' : 'POST',
      headers: getHeaders(),
      body: JSON.stringify(input),
    });
    const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
    if (!res.ok || !data.success) {
      return { ok: false, error: String(data.error || 'Could not save that driver') };
    }
    return { ok: true, driver: (data.driver as CompanyDriver) || null };
  } catch {
    return { ok: false, error: 'No connection — try again' };
  }
}

/**
 * Claim a load AND put a truck on it, in one request.
 *
 * Separate from claimDispatchJob on purpose: that returns { ok, job } and the
 * board reads only `result.job`. This one has to carry who it went to and
 * whether the assignment actually took.
 */
export async function takeLoadForDriver(
  jobId: string,
  driverId: string | null,
): Promise<
  | {
    ok: true;
    job: DispatchJob | null;
    assigned: boolean;
    driverName: string | null;
    assignError: string | null;
    message: string;
  }
  | { ok: false; error: string; reserved?: boolean }
> {
  try {
    let coords: { latitude: number; longitude: number } | null = null;
    try {
      const fix = await acquireDriverFix();
      if (fix.ok) {
        coords = { latitude: fix.pos.coords.latitude, longitude: fix.pos.coords.longitude };
      }
    } catch { /* grocery accept price still works without GPS */ }
    const res = await fetch('/api/haul-jobs', {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ action: 'claim', jobId, proofCapable: true, ...(driverId ? { driverId } : {}), ...coords }),
    });
    const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
    if (!res.ok || !data.success) {
      return { ok: false, error: String(data.error || 'Could not take that load'), reserved: data.reserved === true };
    }
    return {
      ok: true,
      // Same unlocked contact payload claimDispatchJob returns — the board was
      // falling back to the open-board card (no phone/address) and the desk had
      // nothing durable to merge after a mine poll.
      job: (data.job as DispatchJob) || null,
      // Absent when no driver was asked for — a plain owner-operator claim.
      assigned: data.assigned == null ? true : Boolean(data.assigned),
      driverName: data.assignedDriverName != null ? String(data.assignedDriverName) : null,
      assignError: data.assignError != null ? String(data.assignError) : null,
      message: String(data.message || 'Load taken.'),
    };
  } catch {
    return { ok: false, error: 'No connection — try again' };
  }
}

/** Move a load to another driver, or take it back with driverId = null. */
export async function assignJobToDriver(
  jobId: string,
  driverId: string | null,
): Promise<{ ok: true; message: string } | { ok: false; error: string }> {
  try {
    const res = await fetch('/api/haul-jobs/assign', {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ jobId, driverId }),
    });
    const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
    if (!res.ok || !data.success) {
      return { ok: false, error: String(data.error || 'Could not assign that load') };
    }
    return { ok: true, message: String(data.message || 'Assigned.') };
  } catch {
    return { ok: false, error: 'No connection — try again' };
  }
}

/**
 * The loads this company holds, split by whether anybody is on them.
 *
 * Split on assignedAt, NEVER on assignedDriverUserId: an assignment to a driver
 * with no app account writes that column null while the name and truck are set,
 * so it is indistinguishable from unassigned on that field.
 */
export function splitHeldLoads(jobs: DispatchJob[]): {
  needsDriver: DispatchJob[];
  running: DispatchJob[];
} {
  const claimed = jobs.filter((j) => String(j.status) === 'claimed');
  return {
    needsDriver: claimed.filter((j) => !j.assignedAt),
    running: jobs.filter((j) => Boolean(j.assignedAt) || String(j.status) !== 'claimed'),
  };
}

/**
 * Signed-out / network blip is not “you hold zero loads.”
 *
 * Returning jobs: [] on any throw made the hauler desk wipe cards that had just
 * been claimed or assigned — one failed ?mine=1 looked like an empty book and
 * the load vanished until a full reload (if the server still had it at all).
 */
export async function fetchMyDispatchJobsSafe(): Promise<{
  signedIn: boolean;
  jobs: DispatchJob[];
  error?: string;
}> {
  try {
    return { signedIn: true, jobs: await fetchMyDispatchJobs() };
  } catch (e) {
    return {
      signedIn: false,
      jobs: [],
      error: e instanceof Error ? e.message : 'Could not load held loads',
    };
  }
}

/** Keep local rows the server has not echoed yet (claim race, laggy ?mine=1). */
export function mergeHeldJobs(
  previous: DispatchJob[],
  server: DispatchJob[],
): DispatchJob[] {
  const seen = new Set(server.map((j) => j.id));
  return [...previous.filter((j) => !seen.has(j.id)), ...server];
}
